import React, { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { MapContainer, TileLayer, Marker, Popup } from "react-leaflet";
import L from "leaflet";
import dayjs from "dayjs";
import { X } from "react-feather";
import "leaflet/dist/leaflet.css";
import iconRetinaUrl from "leaflet/dist/images/marker-icon-2x.png";
import iconUrl from "leaflet/dist/images/marker-icon.png";
import shadowUrl from "leaflet/dist/images/marker-shadow.png";
import DataTable from "../../../../components/common/DataTable";
import { useGetCustomerLocationsQuery, useGetFieldVisitsGlobalQuery, useGetCustomersQuery } from "../customersApi";
import { useBranchFilter } from "../../../branchFilter/useBranchFilter";
import { paths } from "../../../../routes/paths";

// Webpack doesn't resolve Leaflet's default marker image paths correctly
// out of the box - re-point them at the actual bundled asset URLs, a
// well-known, standard workaround (not something specific to this app).
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({ iconRetinaUrl, iconUrl, shadowUrl });

// Centered roughly over coastal/central Kenya so Mombasa, Nairobi, and
// Systems-branch customers are all visible without panning on first load.
const DEFAULT_CENTER = [-1.5, 38];
const DEFAULT_ZOOM = 6;

// Two views sharing one page (requested follow-up to handoff §19.4):
// "Current Locations" is the original one customer = one pin map; "Visit
// Activity" is the full scan-event history (handoff §19.3.1, the global
// field-visits endpoint added 2026-08-05) filterable by customer and/or
// employee, so staff activity and customer visit history can both be
// tracked from the same screen instead of needing two separate ones.
const CustomerLocationsMapPage = () => {
  const { selectedBranchId } = useBranchFilter();
  const [viewMode, setViewMode] = useState("current");

  // --- Current Locations (unchanged from the original build) ---
  const { data: locations = [], isLoading: isLoadingLocations } = useGetCustomerLocationsQuery(
    { branchId: selectedBranchId ?? undefined },
    { skip: viewMode !== "current" }
  );

  // --- Visit Activity filters ---
  const [custQuery, setCustQuery] = useState("");
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [custDropdownOpen, setCustDropdownOpen] = useState(false);
  const [empCode, setEmpCode] = useState("");
  const [dateFrom, setDateFrom] = useState(dayjs().subtract(30, "day").format("YYYY-MM-DD"));
  const [dateTo, setDateTo] = useState(dayjs().format("YYYY-MM-DD"));

  // Reuses the same customer list endpoint the Customers screen uses -
  // search-driven, never fetched unfiltered (5000+ rows across branches).
  const { data: customerOptions = [] } = useGetCustomersQuery(
    { search: custQuery, branchId: selectedBranchId ?? undefined },
    { skip: viewMode !== "history" || Boolean(selectedCustomer) || custQuery.trim().length < 2 }
  );

  const {
    data: visits = [],
    isLoading: isLoadingVisits,
  } = useGetFieldVisitsGlobalQuery(
    {
      branchId: selectedBranchId ?? undefined,
      custId: selectedCustomer?.custId ?? undefined,
      empCode: empCode.trim() || undefined,
      dateFrom: dateFrom || undefined,
      dateTo: dateTo || undefined,
    },
    { skip: viewMode !== "history" }
  );

  const pickCustomer = (c) => {
    setSelectedCustomer(c);
    setCustQuery(c.custName);
    setCustDropdownOpen(false);
  };
  const clearCustomer = () => {
    setSelectedCustomer(null);
    setCustQuery("");
  };
  const clearFilters = () => {
    clearCustomer();
    setEmpCode("");
    setDateFrom(dayjs().subtract(30, "day").format("YYYY-MM-DD"));
    setDateTo(dayjs().format("YYYY-MM-DD"));
  };

  const visitPoints = useMemo(
    () => visits.filter((v) => v.latitude != null && v.longitude != null),
    [visits]
  );

  const activePoints = viewMode === "current" ? locations : visitPoints;
  const mapCenter = useMemo(() => {
    if (activePoints.length === 0) return DEFAULT_CENTER;
    return [Number(activePoints[0].latitude), Number(activePoints[0].longitude)];
  }, [activePoints]);

  const columns = [
    {
      title: "Scanned At (device)",
      dataIndex: "deviceCapturedAt",
      render: (v) => (v ? dayjs(v).format("DD-MMM-YYYY HH:mm") : "-"),
    },
    {
      title: "Received At (server)",
      dataIndex: "receivedAt",
      render: (v) => (v ? dayjs(v).format("DD-MMM-YYYY HH:mm") : "-"),
    },
    {
      title: "Customer",
      dataIndex: "custName",
      render: (v, record) => (
        <Link to={paths.customerEdit(record.branchId, record.custId)} target="_blank" rel="noopener noreferrer">
          {v || record.custId}
        </Link>
      ),
    },
    { title: "Branch", dataIndex: "branchCode", render: (v) => v || "-" },
    {
      // scannedByEmployeeName only resolves where the scanning account is
      // actually linked to an HR employee (App_Users.EmployeeId) - falls
      // back to the plain account name otherwise (handoff §19.3.1).
      title: "Scanned By",
      dataIndex: "scannedByName",
      render: (_, record) => (
        <>
          {record.scannedByEmployeeName || record.scannedByName || "-"}
          {record.scannedByEmployeeCode != null && (
            <span className="text-muted small"> (Emp #{record.scannedByEmployeeCode})</span>
          )}
        </>
      ),
    },
    { title: "Module", dataIndex: "moduleContext", render: (v) => v || "-" },
    {
      title: "Coordinates",
      dataIndex: "latitude",
      render: (_, record) =>
        record.latitude != null ? `${Number(record.latitude).toFixed(6)}, ${Number(record.longitude).toFixed(6)}` : "-",
    },
    {
      title: "Accuracy",
      dataIndex: "accuracyMeters",
      render: (v) => (v != null ? `±${v}m` : "-"),
    },
  ];

  return (
    <div className="page-wrapper">
      <div className="content">
        <div className="page-header flex-wrap">
          <div className="add-item d-flex">
            <div className="page-title">
              <h4>Customer Locations & Field Activity</h4>
              <h6>
                {viewMode === "current"
                  ? isLoadingLocations
                    ? "Loading..."
                    : `${locations.length} customer(s) with a known location`
                  : isLoadingVisits
                  ? "Loading..."
                  : `${visits.length} visit event(s) matching your filters`}
              </h6>
            </div>
          </div>
          <div className="page-btn">
            <div className="btn-group">
              <button
                type="button"
                className={`btn ${viewMode === "current" ? "btn-primary" : "btn-outline-primary"}`}
                onClick={() => setViewMode("current")}
              >
                Current Locations
              </button>
              <button
                type="button"
                className={`btn ${viewMode === "history" ? "btn-primary" : "btn-outline-primary"}`}
                onClick={() => setViewMode("history")}
              >
                Visit Activity
              </button>
            </div>
          </div>
        </div>

        {viewMode === "history" && (
          <div className="card mb-4">
            <div className="card-body">
              <div className="row g-3 align-items-end">
                <div className="col-md-4" style={{ position: "relative" }}>
                  <label className="form-label mb-1">Customer</label>
                  {selectedCustomer ? (
                    <div className="d-flex align-items-center gap-2">
                      <input type="text" className="form-control" value={selectedCustomer.custName} readOnly disabled />
                      <button type="button" className="btn btn-outline-secondary" onClick={clearCustomer} title="Clear customer filter">
                        <X size={14} />
                      </button>
                    </div>
                  ) : (
                    <input
                      type="text"
                      className="form-control"
                      placeholder="Search customer by name..."
                      value={custQuery}
                      onChange={(e) => {
                        setCustQuery(e.target.value);
                        setCustDropdownOpen(true);
                      }}
                      onFocus={() => setCustDropdownOpen(true)}
                      onBlur={() => setTimeout(() => setCustDropdownOpen(false), 150)}
                      autoComplete="off"
                    />
                  )}
                  {!selectedCustomer && custDropdownOpen && customerOptions.length > 0 && (
                    <div className="card position-absolute w-100 mt-1" style={{ zIndex: 20, maxHeight: 220, overflowY: "auto" }}>
                      <div className="list-group list-group-flush">
                        {customerOptions.map((c) => (
                          <button
                            key={`${c.branchId}-${c.custId}`}
                            type="button"
                            className="list-group-item list-group-item-action py-2 text-start"
                            onMouseDown={(e) => e.preventDefault()}
                            onClick={() => pickCustomer(c)}
                          >
                            <div className="fw-semibold">{c.custName}</div>
                            <div className="text-muted small">{c.branchCode}</div>
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
                <div className="col-md-3">
                  <label className="form-label mb-1">Employee Code</label>
                  <input
                    type="text"
                    className="form-control"
                    placeholder="e.g. 214"
                    value={empCode}
                    onChange={(e) => setEmpCode(e.target.value)}
                  />
                </div>
                <div className="col-md-2">
                  <label className="form-label mb-1">From</label>
                  <input type="date" className="form-control" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} />
                </div>
                <div className="col-md-2">
                  <label className="form-label mb-1">To</label>
                  <input type="date" className="form-control" value={dateTo} onChange={(e) => setDateTo(e.target.value)} />
                </div>
                <div className="col-md-1">
                  <button type="button" className="btn btn-outline-secondary w-100" onClick={clearFilters} title="Reset filters">
                    Reset
                  </button>
                </div>
              </div>
              <p className="text-muted small mb-0 mt-2">
                Employee Code matches within the branch(es) shown above - pick a branch from the header
                picker too if the same code exists in more than one branch. Defaults to the last 30 days;
                widen the date range for older activity.
              </p>
            </div>
          </div>
        )}

        <div className="card mb-4">
          <div className="card-body p-0">
            <MapContainer
              center={activePoints.length > 0 ? mapCenter : DEFAULT_CENTER}
              zoom={activePoints.length > 0 ? 10 : DEFAULT_ZOOM}
              style={{ height: "60vh", width: "100%" }}
            >
              <TileLayer
                attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
              />
              {viewMode === "current"
                ? locations.map((loc) => (
                    <Marker key={`${loc.branchId}-${loc.custId}`} position={[Number(loc.latitude), Number(loc.longitude)]}>
                      <Popup>
                        <div className="mb-1"><strong>{loc.custName}</strong></div>
                        <div className="text-muted small mb-1">
                          {loc.locationUpdatedAt ? dayjs(loc.locationUpdatedAt).format("DD-MMM-YYYY HH:mm") : ""}
                          {loc.locationSourceName ? ` · by ${loc.locationSourceName}` : ""}
                        </div>
                        <Link to={paths.customerEdit(loc.branchId, loc.custId)}>View customer</Link>
                      </Popup>
                    </Marker>
                  ))
                : visitPoints.map((v) => (
                    <Marker key={v.recordGuid} position={[Number(v.latitude), Number(v.longitude)]}>
                      <Popup>
                        <div className="mb-1"><strong>{v.custName}</strong></div>
                        <div className="text-muted small mb-1">
                          {dayjs(v.deviceCapturedAt).format("DD-MMM-YYYY HH:mm")}
                          {" · "}
                          {v.scannedByEmployeeName || v.scannedByName || "-"}
                        </div>
                        <Link to={paths.customerEdit(v.branchId, v.custId)}>View customer</Link>
                      </Popup>
                    </Marker>
                  ))}
            </MapContainer>
          </div>
        </div>

        {viewMode === "history" && (
          <div className="card table-list-card">
            <div className="card-body">
              <div className="table-responsive">
                <DataTable columns={columns} dataSource={visits} rowKey="recordGuid" loading={isLoadingVisits} />
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CustomerLocationsMapPage;
